import type { ConstValues } from '@openx/types/helpers';
import {
  getCountriesFromGroup,
  getRegionFromGroup,
  isGroup,
} from '../components/AudienceCriteria/components/utils/guards';
import { exportType, SELECTIVE_SHARING, US_REGION_CODE } from '../constants';
import type { AudienceForm, FormSegment, Group, GroupLike } from '../types';
import { isGroupReallyEmpty } from './isGroupReallyEmpty';
export const MARKET_OPERATOR_RESTRICTION_REASON = {
  CROSS_REGION: 'cross_region',
  INTERNATIONAL_MULTIPARTNER: 'international_multipartner',
} as const;

export type MarketOperatorRestrictionReason = ConstValues<typeof MARKET_OPERATOR_RESTRICTION_REASON>;

/**
 * Region-first flow is used for new audiences and for audiences that already have international criteria.
 * A template created from a legacy (US-only, no region) audience keeps the legacy flow.
 */
export const getShouldUseRegionFirstFlow = ({
  isCreate,
  isCreateFromTemplate,
  hasInternationalCriteria,
}: {
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
  hasInternationalCriteria: boolean;
}): boolean => (isCreateFromTemplate ? hasInternationalCriteria : !!isCreate || hasInternationalCriteria);

export const isNonUsMarketGroup = (group: Group): boolean => {
  const region = getRegionFromGroup(group);
  const countries = getCountriesFromGroup(group);

  if (countries.length > 0) {
    return true;
  }

  return region != null && region !== US_REGION_CODE;
};

export const isUSMarketGroup = (group: Group): boolean => {
  const region = getRegionFromGroup(group);

  if (region === US_REGION_CODE) {
    return true;
  }

  // Legacy audiences created before region criteria: content with no region/countries is US.
  // Empty placeholder groups are ignored so create-flow defaults stay hidden until US is selected.
  if (region != null || getCountriesFromGroup(group).length > 0) {
    return false;
  }

  return !isGroupReallyEmpty(group);
};

export const hasAnyNonUsMarketGroupInGroupLikes = (groups: GroupLike[] | undefined): boolean => {
  if (!groups?.length) {
    return false;
  }

  return groups.some(node => {
    if ('groups' in node) {
      return hasAnyNonUsMarketGroupInGroupLikes(node.groups);
    }

    return isNonUsMarketGroup(node);
  });
};

export const hasAnyUsMarketGroupInGroupLikes = (groups: GroupLike[] | undefined): boolean => {
  if (!groups?.length) {
    return false;
  }

  return groups.some(node => {
    if ('groups' in node) {
      return hasAnyUsMarketGroupInGroupLikes(node.groups);
    }

    return isUSMarketGroup(node);
  });
};

export const hasAnyNonUsMarketGroupInNormalizedDefinition = (
  normalizedDefinition: AudienceForm['normalized_definition'] | undefined
): boolean => {
  if (!normalizedDefinition) {
    return false;
  }

  return (
    hasAnyNonUsMarketGroupInGroupLikes(normalizedDefinition.includes.groups) ||
    hasAnyNonUsMarketGroupInGroupLikes(normalizedDefinition.excludes.groups)
  );
};

export const hasAnyUsMarketGroupInNormalizedDefinition = (
  normalizedDefinition: AudienceForm['normalized_definition'] | undefined
): boolean => {
  if (!normalizedDefinition) {
    return false;
  }

  return (
    hasAnyUsMarketGroupInGroupLikes(normalizedDefinition.includes.groups) ||
    hasAnyUsMarketGroupInGroupLikes(normalizedDefinition.excludes.groups)
  );
};

/** Target ID types apply only to non-bidstream audiences that target the US market. */
export const shouldShowTargetIdTypes = (
  exportTypeValue: unknown,
  normalizedDefinition: AudienceForm['normalized_definition'] | undefined
): boolean =>
  !!exportTypeValue &&
  exportTypeValue !== exportType.BIDSTREAM &&
  hasAnyUsMarketGroupInNormalizedDefinition(normalizedDefinition);

/**
 * Data sourced from another organization: Data Marketplace (sellable) segments, plus SSS segments
 */
export const isMultipartnerSegment = (segment: FormSegment, accountId: string): boolean =>
  Boolean(accountId) &&
  (segment.sellable || segment.sharing_type === SELECTIVE_SHARING) &&
  segment.account_id !== accountId;

const segmentBuckets = (group: Group): FormSegment[] => {
  const allSegments: FormSegment[] = [];

  for (const segmentField of ['direct', 'predefined', 'location', 'demographic_age', 'demographic_gender'] as const) {
    const bucketSegments = group[segmentField]?.segments;

    if (bucketSegments?.length) {
      allSegments.push(...bucketSegments);
    }
  }

  return allSegments;
};

export const collectNonEmptyLeafGroups = (node: GroupLike): Group[] => {
  if (isGroup(node)) {
    return isGroupReallyEmpty(node) ? [] : [node];
  }

  return node.groups.flatMap(collectNonEmptyLeafGroups);
};

export const collectAllLeafGroups = (node: GroupLike): Group[] => {
  if (isGroup(node)) {
    return [node];
  }

  return node.groups.flatMap(collectAllLeafGroups);
};

const IMPLICIT_NON_US_REGION_SENTINEL = '__implicit_non_us__';

const addMarketCrossRegionCodesFromLeaf = (leaf: Group, regionCodes: Set<string>): void => {
  const region = getRegionFromGroup(leaf);
  const countries = getCountriesFromGroup(leaf);

  if (region !== null) {
    regionCodes.add(region);
    return;
  }

  if (countries.length === 0) {
    regionCodes.add(US_REGION_CODE);
    return;
  }

  regionCodes.add(IMPLICIT_NON_US_REGION_SENTINEL);
};

export const explicitRegionsInGroupLike = (node: GroupLike): Set<string> => {
  const regionCodes = new Set<string>();

  for (const leafGroup of collectAllLeafGroups(node)) {
    addMarketCrossRegionCodesFromLeaf(leafGroup, regionCodes);
  }

  return regionCodes;
};

export const hasCrossRegionAmongSiblings = (siblings: GroupLike[]): boolean => {
  if (siblings.length <= 1) {
    return false;
  }

  const union = new Set<string>();

  for (const sibling of siblings) {
    explicitRegionsInGroupLike(sibling).forEach(regionCode => {
      union.add(regionCode);
    });
  }

  return union.size > 1;
};

const hasInternationalMultipartnerSegmentsAmongSiblings = (siblings: GroupLike[], accountId: string): boolean => {
  if (siblings.length <= 1) {
    return false;
  }

  for (const leafGroup of siblings.flatMap(collectNonEmptyLeafGroups)) {
    if (!isGroup(leafGroup) || !isNonUsMarketGroup(leafGroup)) {
      continue;
    }

    for (const segment of segmentBuckets(leafGroup)) {
      if (isMultipartnerSegment(segment, accountId)) {
        return true;
      }
    }

    if (leafGroup.lookalike?.segments?.length) {
      for (const segment of leafGroup.lookalike.segments) {
        if (isMultipartnerSegment(segment, accountId)) {
          return true;
        }
      }
    }
  }

  return false;
};

export const shouldForceOrOperator = (reason: MarketOperatorRestrictionReason | null): boolean => reason != null;

export const getSiblingOperatorRestrictionReason = (
  siblings: GroupLike[],
  accountId: string,
  includeSellableMultipartner = false
): MarketOperatorRestrictionReason | null => {
  const hasNoSiblings = siblings.length === 0;

  if (hasNoSiblings) {
    return null;
  }

  const hasMultipleSiblings = siblings.length > 1;

  if (hasMultipleSiblings) {
    const hasCrossRegionSiblings = hasCrossRegionAmongSiblings(siblings);

    if (hasCrossRegionSiblings) {
      return MARKET_OPERATOR_RESTRICTION_REASON.CROSS_REGION;
    }

    const hasInternationalMultipartnerSiblings =
      includeSellableMultipartner && hasInternationalMultipartnerSegmentsAmongSiblings(siblings, accountId);

    if (hasInternationalMultipartnerSiblings) {
      return MARKET_OPERATOR_RESTRICTION_REASON.INTERNATIONAL_MULTIPARTNER;
    }

    return null;
  }

  const only = siblings[0];

  if (only == null) {
    return null;
  }

  // For a single top-level node, recurse only when it is a subgroup with multiple children
  // because operator restriction applies to sibling combinations.
  const isSingleTopLevelSubgroupWithMultipleChildren =
    'groups' in only && Array.isArray(only.groups) && only.groups.length > 1;

  if (isSingleTopLevelSubgroupWithMultipleChildren) {
    return getSiblingOperatorRestrictionReason(only.groups, accountId, includeSellableMultipartner);
  }

  return null;
};

export const getAnyIncludesMarketRestrictionReason = (
  includesGroups: GroupLike[] | undefined,
  accountId: string,
  shouldUseRegionFirstFlow: boolean,
  includeSellableMultipartner = false
): MarketOperatorRestrictionReason | null => {
  if (!shouldUseRegionFirstFlow) {
    return null;
  }

  const groups = includesGroups ?? [];
  const top = getSiblingOperatorRestrictionReason(groups, accountId, includeSellableMultipartner);

  if (top != null) {
    return top;
  }

  for (const node of groups) {
    const isSubgroupWithSiblingChildren = 'groups' in node && Array.isArray(node.groups) && node.groups.length > 1;

    if (isSubgroupWithSiblingChildren) {
      const nested = getSiblingOperatorRestrictionReason(node.groups, accountId, includeSellableMultipartner);

      if (nested != null) {
        return nested;
      }
    }
  }

  return null;
};
